import { Autocomplete, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { AutocompleteProps } from "../../../../../utils/types";
import {
  FormAutocomplete,
  formClasses,
  labelClasses,
  LabeledAutocomplete,
} from "../../../../../styles/AutocompleteStyles";
import { useLazyQuery } from "@apollo/client";
import {
  AtsStckGroupOrderField,
  GetStockGroupsResult,
  GetStockGroupsVars,
} from "../itemgroup/queries/types";
import { GetStockGroups } from "../itemgroup/queries/queries";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import { FETCH_MORE_DATA, ROWS_PER_PAGE, debouncedDelay } from "../../../../../utils/constants";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { AtsSubsidiaryOrderField } from "../subsidary/queries/types";
import { getModifiedScrollHeight } from "../../../../../utils/UtilFunctions";
import { GetItemGroups } from "./queries/queries";
import {
  AtsItemGroupEdge,
  AtsItemGroupOrderField,
  GetItemGroupsResult,
  GetItemGroupsVars,
} from "./queries/types";
import { useDebounce } from "../../../../../customHooks/useDebounce";

interface StockGroupsAutoProps extends AutocompleteProps {
  value: number;
  onChange: (newValue: AtsItemGroupEdge) => void;
}
const GroupAutocomplete = ({
  onKeyDown,
  label,
  value,
  onChange,
  autoFocus,
  placeholder,
}: StockGroupsAutoProps) => {
  const [GetGroups, { data, loading, fetchMore }] = useLazyQuery<
    GetItemGroupsResult,
    GetItemGroupsVars
  >(GetItemGroups);

  const [groups, setGroups] = useState<AtsItemGroupEdge[]>([]);
  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [autoValue, setAutoValue] = useState<AtsItemGroupEdge | null>(null);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);

  const { customer_id } = useUrlParams();

  let commonAutocompleteClassName = label
    ? labelClasses.inputRoot
    : formClasses.inputRoot;
  let commonTextFieldClassName = label
    ? labelClasses.formControlRoot
    : formClasses.formControlRoot;

  const Autocomplete =
    label && label.length > 0 ? LabeledAutocomplete : FormAutocomplete;
  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetItemGroups.edges;
            const pageInfo = fetchMoreResult.GetItemGroups.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetItemGroups.edges.filter(
              ({ node: { id } }) =>
                newEdges.findIndex(
                  ({ node: { id: newId } }) => newId === id
                ) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetItemGroups: {
                edges: [...groups, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetItemGroups.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };
  useEffect(() => {
    if (customer_id) {
      GetGroups({
        variables: {
          after: null,
          customerID: customer_id,
          first: ROWS_PER_PAGE,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsItemGroupOrderField.GROUP_NAME,
          },

          name: debouncedSearch,
        },
      });
    }
  }, [customer_id, GetGroups, debouncedSearch]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItemGroups.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = groups.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setGroups(updatedNewData);
      } else {
        setGroups(newData);
      }
      setEndCursor(data.GetItemGroups.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    if (value) {
      const foundValue = groups.find(
        ({ node }) => Number(node.id) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [value, data, groups]);

  return (
    <Autocomplete
      className={commonAutocompleteClassName}
      options={groups}
      onKeyDown={onKeyDown}
      value={autoValue}
      openOnFocus
      forcePopupIcon
      getOptionLabel={(option) => (option as AtsItemGroupEdge).node.groupName}
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      onChange={(e, newValue) => {
        onChange(newValue as AtsItemGroupEdge);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          autoFocus={autoFocus}
          onChange={(e) => setSearchData(e.target.value)}
          className={commonTextFieldClassName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={placeholder}
        />
      )}
    />
  );
};

export default GroupAutocomplete;
